// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import { useMemo, useState } from 'react';
import { Alert, Badge, Button, IconButton, AppIcon, Select, TextInput } from '@loa/ui-components';
import { chartPalette, colors } from '@loa/design-tokens';

// Section dots reuse the token chart palette.
const chart = (i: number): string => chartPalette[i % chartPalette.length];
import {
  relationshipCashflowApi,
  SCENARIO_ASSUMPTION_KEYS,
  type Scenario,
  type ScenarioOverride,
} from '../../api/relationshipCashflowApi';
import type { AccountSummary, FigureOption } from '../../api/financialPositionApi';
import { ACCENT_TEAL } from './CashflowIncomeChart';

/**
 * The workspace's "Parameters" tab (cashflow-modelling-tab PRD §5.2, editor
 * redesign 2026-07): the open forecast's overrides grouped into INCOME /
 * SPENDING / ACCOUNTS & ASSETS / ASSUMPTIONS & POLICY sections — each item a
 * card with a kind badge and a remove × — plus the ADD OVERRIDE toggle grid
 * with one compact form per kind. Disposals and sweeps are AI-editor-only in
 * this surface (the legacy Overview modal still offers them manually).
 */

type AddKind = 'cashflow' | 'account' | 'asset' | 'assumption' | 'figure' | 'policy';

interface Props {
  profileId: string;
  scenario: Scenario;
  figures: FigureOption[];
  sweepAccounts: AccountSummary[];
  onChanged: () => void;
}

const euro = (v: number | string | undefined | null): string => {
  const n = Number(v);
  return Number.isNaN(n) ? String(v ?? '') : `€${n.toLocaleString('en-IE')}`;
};

const yearOf = (validFrom: string | null | undefined): string =>
  validFrom ? validFrom.slice(0, 4) : '';

interface Item {
  override: ScenarioOverride;
  title: string;
  value: string;
  badge: string;
  badgeColor: 'purple' | 'neutral' | 'teal' | 'green' | 'grey' | 'orange' | 'blue';
}

interface Section {
  key: string;
  label: string;
  dot: string;
  items: Item[];
}

export function buildSections(
  overrides: ScenarioOverride[],
  figures: FigureOption[],
  sweepAccounts: AccountSummary[],
): Section[] {
  const figure = (key: string | null) => figures.find((f) => f.key === key);
  const accountLabel = (ref: string | null | undefined) => {
    const id = String(ref ?? '').replace('account:', '');
    const acct = sweepAccounts.find((a) => a.id === id);
    return acct ? acct.institutionName ?? acct.accountType : 'account';
  };

  const income: Item[] = [];
  const spending: Item[] = [];
  const accounts: Item[] = [];
  const policy: Item[] = [];

  for (const o of overrides) {
    const p = o.payload ?? {};
    const year = yearOf(o.validFrom);
    switch (o.kind) {
      case 'CASHFLOW_ADD': {
        const isIncome = p.direction === 'INCOME';
        const oneOff = p.endYear != null && String(p.endYear) === year;
        (isIncome ? income : spending).push({
          override: o,
          title: String(p.label ?? 'Scenario item'),
          value: `${euro(p.amount as number)}${oneOff ? '' : ' / yr'} from ${year}`,
          badge: isIncome ? 'Income' : 'Spending',
          badgeColor: isIncome ? 'purple' : 'neutral',
        });
        break;
      }
      case 'FIGURE': {
        const f = figure(o.figureRef);
        const isIncome = !f || f.kind === 'INCOME';
        (isIncome ? income : spending).push({
          override: o,
          title: f?.label ?? String(o.figureRef),
          value: `${euro(o.amount)} / yr from ${year}`,
          badge: isIncome ? 'Income' : 'Spending',
          badgeColor: isIncome ? 'purple' : 'neutral',
        });
        break;
      }
      case 'ACCOUNT_ADD':
        accounts.push({
          override: o,
          title: String(p.label ?? p.accountType ?? 'Account'),
          value: `${euro(p.balance as number)} from ${year}`,
          badge: 'Account',
          badgeColor: 'green',
        });
        break;
      case 'ASSET_ADD':
        accounts.push({
          override: o,
          title: String(p.name ?? p.assetType ?? 'Asset'),
          value: `${euro(p.value as number)} from ${year}`,
          badge: 'Asset',
          badgeColor: 'teal',
        });
        break;
      case 'DISPOSAL':
        accounts.push({
          override: o,
          title: `Sell ${String(o.figureRef ?? p.targetRef ?? 'item').split(':')[0]}`,
          value: `in ${year}`,
          badge: 'Sale',
          badgeColor: 'orange',
        });
        break;
      case 'SWEEP_SURPLUS':
        accounts.push({
          override: o,
          title: `Sweep surplus → ${accountLabel(o.figureRef)}`,
          value: `Auto from ${year}`,
          badge: 'Rule',
          badgeColor: 'grey',
        });
        break;
      case 'SWEEP_RULE':
        accounts.push({
          override: o,
          title: `Sweep ${accountLabel(p.fromRef as string)} → ${accountLabel(p.toRef as string)}`,
          value: `over ${euro(p.thresholdBalance as number)} from ${year}`,
          badge: 'Rule',
          badgeColor: 'grey',
        });
        break;
      case 'ASSUMPTION': {
        const meta = SCENARIO_ASSUMPTION_KEYS.find((k) => k.key === o.assumptionKey);
        policy.push({
          override: o,
          title: meta?.label ?? String(o.assumptionKey),
          value: `${o.amount}${meta?.unit ?? ''} from ${year}`,
          badge: 'Assumption',
          badgeColor: 'purple',
        });
        break;
      }
      default: {
        // POLICY_GROWTH / POLICY_CONTRIBUTION / POLICY_VALUE
        const f = figure(o.figureRef);
        const setting = o.kind.replace('POLICY_', '').toLowerCase();
        policy.push({
          override: o,
          title: `${f?.label ?? o.figureRef} ${setting}`,
          value: `${o.kind === 'POLICY_GROWTH' ? `${o.amount}%` : euro(o.amount)} from ${year}`,
          badge: 'Policy',
          badgeColor: 'blue',
        });
      }
    }
  }

  return [
    { key: 'income', label: 'Income', dot: colors.purplePrimary, items: income },
    { key: 'spending', label: 'Spending', dot: chart(3), items: spending },
    { key: 'accounts', label: 'Accounts & Assets', dot: ACCENT_TEAL, items: accounts },
    { key: 'policy', label: 'Assumptions & Policy', dot: chart(2), items: policy },
  ].filter((s) => s.items.length > 0);
}

export interface BaseParam {
  key: string;
  label: string;
  value: string;
  kind: FigureOption['kind'];
  overridden: boolean;
}

const formatFigureValue = (f: FigureOption): string => {
  if (f.baseValue == null) return '—';
  const money = euro(f.baseValue);
  if (f.kind === 'INCOME' || f.kind === 'EXPENDITURE') {
    return f.frequency && f.frequency !== 'ANNUALLY'
      ? `${money} / ${f.frequency.toLowerCase()}`
      : `${money} / yr`;
  }
  return money; // pension / investment current value
};

/**
 * The base parameters that actually fed the projection (before any override) —
 * one row per figure with its resolved value, flagged when the open forecast
 * overrides it (cashflow-remodelling Phase 6). This is the "actual parameters
 * that went into the calculation" the panel shows alongside the overrides.
 */
export function buildBaseParameters(
  figures: FigureOption[],
  overrides: ScenarioOverride[],
): BaseParam[] {
  const overriddenKeys = new Set(
    overrides
      .filter((o) => o.figureRef != null && (o.kind === 'FIGURE' || o.kind.startsWith('POLICY')))
      .map((o) => o.figureRef as string),
  );
  return figures.map((f) => ({
    key: f.key,
    label: f.label,
    value: formatFigureValue(f),
    kind: f.kind,
    overridden: overriddenKeys.has(f.key),
  }));
}


export function ScenarioParametersPanel({ profileId, scenario, figures, sweepAccounts, onChanged }: Props) {
  const [addKind, setAddKind] = useState<AddKind | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // form state (per-kind, reset on submit)
  const [cfDirection, setCfDirection] = useState<'INCOME' | 'EXPENDITURE'>('INCOME');
  const [cfLabel, setCfLabel] = useState('');
  const [cfAmount, setCfAmount] = useState('');
  const [cfFrom, setCfFrom] = useState('');
  const [cfTo, setCfTo] = useState('');
  const [acctName, setAcctName] = useState('');
  const [acctBalance, setAcctBalance] = useState('');
  const [acctFrom, setAcctFrom] = useState('');
  const [assetName, setAssetName] = useState('');
  const [assetValue, setAssetValue] = useState('');
  const [assetGrowth, setAssetGrowth] = useState('');
  const [assetFrom, setAssetFrom] = useState('');
  const [assumptionKey, setAssumptionKey] = useState(SCENARIO_ASSUMPTION_KEYS[0].key);
  const [assumptionValue, setAssumptionValue] = useState('');
  const [assumptionFrom, setAssumptionFrom] = useState('');
  const [figureRef, setFigureRef] = useState('');
  const [figureAmount, setFigureAmount] = useState('');
  const [figureFrom, setFigureFrom] = useState('');
  const [policyRef, setPolicyRef] = useState('');
  const [policySetting, setPolicySetting] = useState('GROWTH');
  const [policyValue, setPolicyValue] = useState('');
  const [policyFrom, setPolicyFrom] = useState('');

  const sections = useMemo(
    () => buildSections(scenario.overrides, figures, sweepAccounts),
    [scenario.overrides, figures, sweepAccounts],
  );
  const baseParams = useMemo(
    () => buildBaseParameters(figures, scenario.overrides),
    [figures, scenario.overrides],
  );
  const incomeExpenditureFigures = figures.filter((f) => f.kind === 'INCOME' || f.kind === 'EXPENDITURE');
  const policyFigures = figures.filter((f) => f.kind === 'PENSION' || f.kind === 'INVESTMENT');

  // Click a base parameter → open the matching override form pre-targeted to that
  // figure (income/spend → figure override; pension/investment → policy override).
  // This is the "click an item and suggest an override action" interaction; the
  // richer Sell / Downsize / Loss-of-employment cascades are an AI-editor follow-up.
  const overrideParameter = (param: BaseParam) => {
    setError(null);
    if (param.kind === 'PENSION' || param.kind === 'INVESTMENT') {
      setPolicyRef(param.key);
      setAddKind('policy');
    } else {
      setFigureRef(param.key);
      setAddKind('figure');
    }
  };

  const run = (call: Promise<unknown>, clear: () => void) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    call
      .then(() => {
        clear();
        onChanged();
      })
      .catch(() => setError('Could not add that change.'))
      .finally(() => setBusy(false));
  };

  const removeOverride = (overrideId: string) => {
    if (busy) return;
    setBusy(true);
    relationshipCashflowApi
      .removeScenarioOverride(profileId, scenario.id, overrideId)
      .then(onChanged)
      .catch(() => setError('Could not remove that change.'))
      .finally(() => setBusy(false));
  };

  const submitters: Record<AddKind, () => void> = {
    cashflow: () =>
      run(
        relationshipCashflowApi.addScenarioCashflowItem(profileId, scenario.id, {
          validFrom: cfFrom,
          direction: cfDirection,
          category: null,
          label: cfLabel.trim(),
          amount: Number(cfAmount),
          frequency: 'ANNUALLY',
          ownerClientId: null,
          endYear: cfTo === '' ? null : Number(cfTo.slice(0, 4)),
        }),
        () => {
          setCfLabel('');
          setCfAmount('');
          setCfFrom('');
          setCfTo('');
        },
      ),
    account: () =>
      run(
        relationshipCashflowApi.addScenarioAccount(profileId, scenario.id, {
          validFrom: acctFrom,
          accountType: 'SAVINGS',
          classification: 'ASSET',
          balance: Number(acctBalance),
          interestRate: null,
          monthlyRepayment: null,
          ownerClientId: null,
          label: acctName.trim() === '' ? null : acctName.trim(),
        }),
        () => {
          setAcctName('');
          setAcctBalance('');
          setAcctFrom('');
        },
      ),
    asset: () =>
      run(
        relationshipCashflowApi.addScenarioAsset(profileId, scenario.id, {
          validFrom: assetFrom,
          assetType: 'PROPERTY',
          name: assetName.trim() === '' ? null : assetName.trim(),
          value: Number(assetValue),
          assumedGrowthRate: assetGrowth === '' ? null : Number(assetGrowth),
          ownerClientId: null,
        }),
        () => {
          setAssetName('');
          setAssetValue('');
          setAssetGrowth('');
          setAssetFrom('');
        },
      ),
    assumption: () =>
      run(
        relationshipCashflowApi.addScenarioAssumptionOverride(profileId, scenario.id, {
          assumptionKey,
          validFrom: assumptionFrom,
          value: Number(assumptionValue),
        }),
        () => {
          setAssumptionValue('');
          setAssumptionFrom('');
        },
      ),
    figure: () =>
      run(
        relationshipCashflowApi.addScenarioOverride(profileId, scenario.id, {
          figureRef,
          validFrom: figureFrom,
          amount: Number(figureAmount),
          frequency: null,
        }),
        () => {
          setFigureAmount('');
          setFigureFrom('');
        },
      ),
    policy: () =>
      run(
        relationshipCashflowApi.addScenarioPolicyOverride(profileId, scenario.id, {
          policyRef,
          setting: policySetting,
          validFrom: policyFrom,
          value: Number(policyValue),
          mode: policySetting === 'VALUE' ? 'DELTA' : null,
        }),
        () => {
          setPolicyValue('');
          setPolicyFrom('');
        },
      ),
  };

  const toggles: Array<{ kind: AddKind; label: string }> = [
    { kind: 'cashflow', label: 'Income / spending' },
    { kind: 'account', label: 'Account' },
    { kind: 'asset', label: 'Asset' },
    { kind: 'assumption', label: 'Assumption' },
    { kind: 'figure', label: 'Figure override' },
    { kind: 'policy', label: 'Policy override' },
  ];

  const field = 'flex flex-col gap-xs text-body-04 text-grey-04';

  return (
    <div data-testid="scenario-parameters-panel" className="flex flex-col gap-md">
      {error && <Alert variant="error">{error}</Alert>}

      {baseParams.length > 0 && (
        <section data-testid="param-section-base">
          <div className="flex items-center gap-sm mb-sm">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: colors.grey03 }} />
            <span className="text-body-04 font-medium text-grey-04 uppercase tracking-wide">
              Base parameters
            </span>
            <span className="text-body-04 text-grey-03">{baseParams.length}</span>
          </div>
          <div className="flex flex-col gap-sm">
            {baseParams.map((p) => (
              <div
                key={p.key}
                data-testid="base-param-item"
                className="rounded-lg border border-grey-06 bg-white px-md py-sm flex items-start justify-between gap-sm"
              >
                <div className="min-w-0">
                  <p className="text-body-03 font-medium text-black m-0">{p.label}</p>
                  <p className="text-body-04 text-grey-03 font-mono m-0">{p.value}</p>
                </div>
                <div className="flex items-center gap-xs shrink-0">
                  {p.overridden && <Badge color="orange" size="sm">Overridden</Badge>}
                  <Button
                    variant="link"
                    size="sm"
                    data-testid={`base-param-override-${p.key}`}
                    onClick={() => overrideParameter(p)}
                  >
                    Override
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {sections.length === 0 && (
        <p data-testid="parameters-empty" className="text-body-03 text-grey-03 m-0">
          No changes yet — add one below or ask the AI editor.
        </p>
      )}

      {sections.map((section) => (
        <section key={section.key} data-testid={`param-section-${section.key}`}>
          <div className="flex items-center gap-sm mb-sm">
            <span className="w-2 h-2 rounded-full inline-block" style={{ backgroundColor: section.dot }} />
            <span className="text-body-04 font-medium text-grey-04 uppercase tracking-wide">
              {section.label}
            </span>
            <span className="text-body-04 text-grey-03">{section.items.length}</span>
          </div>
          <div className="flex flex-col gap-sm">
            {section.items.map(({ override, title, value, badge, badgeColor }) => (
              <div
                key={override.id}
                data-testid="param-item"
                className="rounded-lg border border-grey-06 bg-white px-md py-sm flex items-start justify-between gap-sm"
              >
                <div className="min-w-0">
                  <p className="text-body-03 font-medium text-black m-0">{title}</p>
                  <p className="text-body-04 text-purple-primary font-mono m-0">{value}</p>
                </div>
                <div className="flex items-center gap-xs shrink-0">
                  <Badge color={badgeColor} size="sm">{badge}</Badge>
                  <IconButton
                    variant="ghost"
                    size="xs"
                    aria-label={`Remove ${title}`}
                    data-testid={`param-remove-${override.id}`}
                    disabled={busy}
                    onClick={() => removeOverride(override.id)}
                    icon={<AppIcon name="close" size={12} color={colors.grey03} />}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}

      <div>
        <p className="text-body-04 font-medium text-grey-04 uppercase tracking-wide mb-sm">Add override</p>
        <div className="grid grid-cols-2 gap-sm">
          {toggles.map(({ kind, label }) => {
            const active = addKind === kind;
            return (
              <button
                key={kind}
                type="button"
                data-testid={`add-toggle-${kind}`}
                onClick={() => setAddKind(active ? null : kind)}
                className={`flex items-center gap-sm rounded-lg border px-sm py-sm text-body-03 cursor-pointer text-left ${
                  active
                    ? 'border-purple-primary bg-lilac-05 text-purple-primary'
                    : 'border-grey-05 bg-white text-grey-01'
                }`}
              >
                <AppIcon name={active ? 'minus' : 'plus'} size={14} color={colors.purplePrimary} />
                {label}
              </button>
            );
          })}
        </div>

        {addKind && (
          <form
            data-testid={`add-form-${addKind}`}
            className="mt-sm rounded-lg bg-lilac-06 p-md flex flex-col gap-sm"
            onSubmit={(e) => {
              e.preventDefault();
              submitters[addKind]();
            }}
          >
            {addKind === 'cashflow' && (
              <>
                <label className={field}>
                  Type
                  <Select
                    data-testid="cf-type"
                    value={cfDirection}
                    onChange={(e) => setCfDirection(e.target.value as 'INCOME' | 'EXPENDITURE')}
                    options={[
                      { value: 'INCOME', text: 'Income' },
                      { value: 'EXPENDITURE', text: 'Spending' },
                    ]}
                  />
                </label>
                <div className="grid grid-cols-2 gap-sm">
                  <label className={field}>
                    Label
                    <TextInput data-testid="cf-label" value={cfLabel} placeholder="e.g. Consulting"
                      onChange={(e) => setCfLabel(e.target.value)} />
                  </label>
                  <label className={field}>
                    Amount / yr
                    <TextInput data-testid="cf-amount" type="number" value={cfAmount} placeholder="€"
                      onChange={(e) => setCfAmount(e.target.value)} />
                  </label>
                  <label className={field}>
                    From
                    <TextInput data-testid="cf-from" type="date" value={cfFrom}
                      onChange={(e) => setCfFrom(e.target.value)} />
                  </label>
                  <label className={field}>
                    To (optional)
                    <TextInput data-testid="cf-to" type="date" value={cfTo}
                      onChange={(e) => setCfTo(e.target.value)} />
                  </label>
                </div>
              </>
            )}

            {addKind === 'account' && (
              <>
                <label className={field}>
                  Account name
                  <TextInput data-testid="acct-name" value={acctName} placeholder="e.g. ISA"
                    onChange={(e) => setAcctName(e.target.value)} />
                </label>
                <div className="grid grid-cols-2 gap-sm">
                  <label className={field}>
                    Opening balance
                    <TextInput data-testid="acct-balance" type="number" value={acctBalance} placeholder="€"
                      onChange={(e) => setAcctBalance(e.target.value)} />
                  </label>
                  <label className={field}>
                    From
                    <TextInput data-testid="acct-from" type="date" value={acctFrom}
                      onChange={(e) => setAcctFrom(e.target.value)} />
                  </label>
                </div>
              </>
            )}

            {addKind === 'asset' && (
              <>
                <label className={field}>
                  Asset name
                  <TextInput data-testid="asset-name" value={assetName} placeholder="e.g. Buy-to-let"
                    onChange={(e) => setAssetName(e.target.value)} />
                </label>
                <div className="grid grid-cols-2 gap-sm">
                  <label className={field}>
                    Value
                    <TextInput data-testid="asset-value" type="number" value={assetValue} placeholder="€"
                      onChange={(e) => setAssetValue(e.target.value)} />
                  </label>
                  <label className={field}>
                    Growth %
                    <TextInput data-testid="asset-growth" type="number" value={assetGrowth} placeholder="%"
                      onChange={(e) => setAssetGrowth(e.target.value)} />
                  </label>
                </div>
                <label className={field}>
                  From
                  <TextInput data-testid="asset-from" type="date" value={assetFrom}
                    onChange={(e) => setAssetFrom(e.target.value)} />
                </label>
              </>
            )}

            {addKind === 'assumption' && (
              <>
                <label className={field}>
                  Assumption
                  <Select
                    data-testid="assumption-key"
                    value={assumptionKey}
                    onChange={(e) => setAssumptionKey(e.target.value)}
                    options={SCENARIO_ASSUMPTION_KEYS.map((k) => ({ value: k.key, text: k.label }))}
                  />
                </label>
                <div className="grid grid-cols-2 gap-sm">
                  <label className={field}>
                    New value
                    <TextInput data-testid="assumption-value" type="number" value={assumptionValue} placeholder="%"
                      onChange={(e) => setAssumptionValue(e.target.value)} />
                  </label>
                  <label className={field}>
                    From
                    <TextInput data-testid="assumption-from" type="date" value={assumptionFrom}
                      onChange={(e) => setAssumptionFrom(e.target.value)} />
                  </label>
                </div>
              </>
            )}

            {addKind === 'figure' && (
              <>
                <label className={field}>
                  Figure
                  <Select
                    data-testid="figure-ref"
                    value={figureRef}
                    onChange={(e) => setFigureRef(e.target.value)}
                    options={[
                      { value: '', text: 'Select…' },
                      ...incomeExpenditureFigures.map((f) => ({ value: f.key, text: f.label })),
                    ]}
                  />
                </label>
                <div className="grid grid-cols-2 gap-sm">
                  <label className={field}>
                    Amount
                    <TextInput data-testid="figure-amount" type="number" value={figureAmount} placeholder="€"
                      onChange={(e) => setFigureAmount(e.target.value)} />
                  </label>
                  <label className={field}>
                    From
                    <TextInput data-testid="figure-from" type="date" value={figureFrom}
                      onChange={(e) => setFigureFrom(e.target.value)} />
                  </label>
                </div>
              </>
            )}

            {addKind === 'policy' && (
              <>
                <label className={field}>
                  Pension / investment
                  <Select
                    data-testid="policy-ref"
                    value={policyRef}
                    onChange={(e) => setPolicyRef(e.target.value)}
                    options={[
                      { value: '', text: 'Select…' },
                      ...policyFigures.map((f) => ({ value: f.key, text: f.label })),
                    ]}
                  />
                </label>
                <label className={field}>
                  Setting
                  <Select
                    data-testid="policy-setting"
                    value={policySetting}
                    onChange={(e) => setPolicySetting(e.target.value)}
                    options={[
                      { value: 'GROWTH', text: 'Growth rate (%)' },
                      { value: 'CONTRIBUTION', text: 'Contribution (€/yr)' },
                      { value: 'VALUE', text: 'Value (€)' },
                    ]}
                  />
                </label>
                <div className="grid grid-cols-2 gap-sm">
                  <label className={field}>
                    Value
                    <TextInput data-testid="policy-value" type="number" value={policyValue}
                      onChange={(e) => setPolicyValue(e.target.value)} />
                  </label>
                  <label className={field}>
                    From
                    <TextInput data-testid="policy-from" type="date" value={policyFrom}
                      onChange={(e) => setPolicyFrom(e.target.value)} />
                  </label>
                </div>
              </>
            )}

            <div>
              <Button size="sm" type="submit" data-testid="add-to-scenario" disabled={busy}>
                Add to scenario
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default ScenarioParametersPanel;
